import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useLocation } from 'react-router-dom';
import {
  ShowChart as ChartIcon,
  FormatListBulleted as FormatListBulletedIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  LocalAtm,
  MailOutline,
} from '@mui/icons-material';
import { wmPalette } from '../../../Theme/WorldSportsGroupTheme';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from '../../../Contexts/AuthContext';
import { PageInfo, PagePermissions } from '../../../Consts/routePermissions';

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.text.primary,
  },
  selectedLink: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  icon: {
    color: theme.palette.text.primary,
  },
  button: {
    '&:hover': {
      backgroundColor: wmPalette.grey[20],
    },
  },
  green: {
    color: wmPalette.green[70],
  },
  grey: {
    color: wmPalette.black[50],
  },
}));

const urlPrefix = (eventId, editionId) =>
  `/app/manage-event/${eventId}/${editionId}/`;
const menuItems = (eventId, editionId) =>
  [
    {
      icon: <ChartIcon />,
      label: 'Dashboard',
      extension: 'dashboard',
      pageId: PageInfo.Dashboard.id,
    },
    {
      icon: <FormatListBulletedIcon />,
      label: 'Order management',
      extension: 'orders',
      pageId: PageInfo.Orders.id,
    },
    {
      icon: <MailOutline />,
      label: 'Email preferences',
      extension: 'email-preferences',
      pageId: PageInfo.EmailPreferences.id,
    },
    {
      icon: <LinkIcon />,
      label: 'Integration',
      extension: 'integration',
      pageId: PageInfo.Integration.id,
    },
    {
      icon: <LocalAtm />,
      label: 'Coupons',
      extension: 'coupons',
      pageId: PageInfo.Coupons.id,
    },
    {
      icon: <EditIcon />,
      label: 'Event information',
      subMenu: [
        {
          label: 'Basic Info',
          extension: 'basicinfo',
          pageId: PageInfo.BasicInfo.id,
        },
        {
          label: 'Course Details',
          extension: 'coursedetails',
          pageId: PageInfo.CourseDetails.id,
        },
        {
          label: "What's included",
          extension: 'whats-included',
          pageId: PageInfo.WhatsIncluded.id,
        },
        {
          label: 'Tickets',
          extension: 'tickets',
          pageId: PageInfo.Tickets.id,
        },
        {
          label: 'Add-ons',
          extension: 'add-ons',
          pageId: PageInfo.AddOns.id,
        },
        {
          label: 'Registration Form',
          extension: 'regform',
          pageId: PageInfo.RegistrationForm.id,
        },
        {
          label: 'Confirmation email',
          extension: 'confirmationemail',
          pageId: PageInfo.ConfirmationEmail.id,
        },
        {
          label: 'FAQs',
          extension: 'faqs',
          pageId: PageInfo.Faqs.id,
        },
      ],
    },
  ].map((item) => ({
    ...item,
    url: item.extension
      ? `${urlPrefix(eventId, editionId)}${item.extension}`
      : undefined,
    subMenu: item.subMenu?.map((subItem) => ({
      ...subItem,
      url: `${urlPrefix(eventId, editionId)}${subItem.extension}`,
    })),
  }));
const requiredFields = {
  basicinfo: [
    'eventTitle',
    'eventType',
    'eventLocation',
    'eventStartDate',
    'eventEndDate',
    'eventSaleStartDate',
    'eventSaleEndDate',
    'eventImages',
    'eventDescription',
    'eventCurrency',
  ],
  courseDetails: [
    'courseHighlights',
    'courseMap',
    'courseProfile',
    'courseSurface',
  ],
  confirmationemail: ['supportEmail'],
};

const EventNavItems = ({ eventInfo = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const classes = useStyles();
  const { pathname } = useLocation();
  const splitPath = pathname.split('/');
  const slug = splitPath[5];
  const editionId = splitPath[4];
  const eventId = splitPath[3];
  const menuItemPermissions = Object.values(PagePermissions).map(
    ({ id, blockedPermissions }) => ({
      pageId: id,
      blockedPermissions,
    })
  );
  const items = menuItems(eventId, editionId).map((item) => {
    const menuItem = { ...item };
    if (item.subMenu) {
      menuItem.subMenu = item.subMenu.map((subItem) => ({
        ...subItem,
        blockedPermissions: menuItemPermissions.find(
          (r) => r.pageId === subItem.pageId
        )?.blockedPermissions,
      }));
    }
    return {
      ...menuItem,
      blockedPermissions: menuItemPermissions.find(
        (r) => r.pageId === item.pageId
      )?.blockedPermissions,
    };
  });
  const eventInfoTabs = items
    .find((x) => x.label === 'Event information')
    .subMenu.map((y) => y.extension);
  const [activeSubMenu, toggleSubMenu] = useState(
    eventInfoTabs.some((x) => x === slug) ? undefined : 'Event information'
  );
  const { isAdmin } = useAuth();

  const toggle = (label) => () => {
    toggleSubMenu(activeSubMenu === label ? undefined : label);
  };

  const iconProps = (active) => ({
    color: 'disabled',
    className: active ? classes.icon : '',
  });

  const isComplete = (extension) => {
    let isComplete = true;
    switch (extension) {
      case 'basicinfo':
        requiredFields.basicinfo.forEach((value) => {
          if (!eventInfo.basicInfo?.hasOwnProperty(value)) {
            isComplete = false;
          }
        });
        break;
      case 'coursedetails':
        isComplete = false;
        if (eventInfo.courseDetails !== undefined) {
          Object.values(eventInfo.courseDetails)?.forEach((value) => {
            if (value?.length > 0) isComplete = true;
          });
        }
        break;
      case 'tickets':
        if (eventInfo.tickets?.length === 0) isComplete = false;
        break;
      case 'add-ons':
        if (eventInfo.addOns?.length === 0) isComplete = false;
        break;
      case 'whats-offered':
        isComplete = eventInfo.offeredServices.length > 0;
        break;
      case 'regform':
        break;
      case 'confirmationemail':
        requiredFields.confirmationemail.forEach((value) => {
          if (!eventInfo.additionalInfo?.hasOwnProperty(value)) {
            isComplete = false;
          }
        });
        break;
      case 'faqs':
        if (eventInfo.faqs?.length === 0) isComplete = false;
        break;
      default:
        break;
    }
    return isComplete;
  };

  const mapSubMenuItem = ({ label, icon, subMenu, blockedPermissions }) => {
    const active = !!subMenu.find((x) => x.extension === slug);
    const filteredSubMenu = subMenu.filter(
      (item) =>
        !item.blockedPermissions ||
        !item.blockedPermissions.includes(eventInfo.permissionLevel) ||
        isAdmin
    );
    if (filteredSubMenu.length === 0) return null;
    return (
      <React.Fragment key={label}>
        <ListItem
          button
          key={label}
          selected={active}
          className={(active ? classes.selectedLink : '', classes.button)}
          onClick={toggle(label)}
        >
          {icon && <ListItemIcon {...iconProps(active)}>{icon}</ListItemIcon>}
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              className: active ? classes.selectedLink : classes.link,
            }}
          />
          {activeSubMenu === label ? (
            <ExpandLessIcon {...iconProps()} />
          ) : (
            <ExpandMoreIcon {...iconProps()} />
          )}
        </ListItem>
        <Collapse in={activeSubMenu === label} timeout="auto" unmountOnExit>
          <List disablePadding>
            {subMenu &&
              subMenu.map((x) => mapMenuItem(x, true, isComplete, isMobile))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const mapMenuItem = (
    { label, url, extension, icon, blockedPermissions },
    isWithinSubMenu = false,
    isComplete,
    isMobile
  ) => {
    const active = extension === slug;
    if (
      blockedPermissions &&
      blockedPermissions.includes(eventInfo.permissionLevel) &&
      !isAdmin
    )
      return null;
    return (
      <ListItem
        button
        key={label}
        selected={!isWithinSubMenu && active}
        component={Link}
        to={url}
        color="primary"
        className={classes.button}
      >
        {(icon || isWithinSubMenu) && (
          <ListItemIcon {...iconProps(active)}>{icon}</ListItemIcon>
        )}
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            className: active ? classes.selectedLink : classes.link,
          }}
        />
        {isWithinSubMenu &&
          !isMobile &&
          (isComplete(extension) ? (
            <CheckCircleIcon className={classes.green} />
          ) : (
            <RadioButtonUncheckedIcon className={classes.grey} />
          ))}
      </ListItem>
    );
  };

  return (
    <>
      {items.map((item) =>
        !!item.subMenu ? mapSubMenuItem(item) : mapMenuItem(item)
      )}
    </>
  );
};

export default EventNavItems;
